// The scanner launch record (Option 2, plan sections 0 and 1).
//
// When a scan link opens the Android scanner, this browser profile writes a
// record of the launch: its nonce, the owner it was made for and the targets
// it offered. A batch the scanner sends back carries that nonce in its
// destination, and the scan import page writes WITHOUT a tap only when the
// nonce matches a live record of THIS profile, for the CURRENT owner, and the
// destination's id was one of the record's targets (a new binder has no id:
// the nonce and the owner decide). Everything else (a
// legacy batch with no destination, a crafted link, an expired or missing
// record, another owner) gets a confirm button.
//
// The record is NOT single-use: split uploads share one nonce, a second tab
// may open the same batch, and the writes themselves are exactly-once on the
// server (the batch claim plus its op_key) or through the deck handoff
// ledger. The nonce only answers "may this write start without a tap".
//
// localStorage can be missing or throw: every access is guarded, and an
// unreadable store means no record, so the page asks.

/* eslint-disable i18next/no-literal-string -- storage key and kind codes */
export const SCAN_LAUNCH_KEY = 'cb.scanLaunch.v1'
const KIND_COLLECTION = 'collection'
const KIND_BINDER = 'binder'
const KIND_DECK = 'deck'
/* eslint-enable i18next/no-literal-string */
/** How many launches are remembered (the newest). */
export const SCAN_LAUNCH_KEEP = 5
/** How long a launch may authorize a write. */
export const SCAN_LAUNCH_TTL_MS = 12 * 60 * 60 * 1000

export interface ScanLaunchRecord {
  nonce: string
  /** The owner the launch was made for: the user id, or 'guest'. */
  ownerKey: string
  binderIds: string[]
  deckIds: string[]
  collectionId: string | null
  createdAt: number
}

function strList(v: unknown): string[] | null {
  if (!Array.isArray(v)) return null
  return v.every(x => typeof x === 'string') ? (v as string[]) : null
}

function readRecord(v: unknown): ScanLaunchRecord | null {
  if (!v || typeof v !== 'object') return null
  const r = v as Record<string, unknown>
  const binderIds = strList(r.binderIds)
  const deckIds = strList(r.deckIds)
  if (typeof r.nonce !== 'string' || !r.nonce || typeof r.ownerKey !== 'string' || !r.ownerKey) return null
  if (!binderIds || !deckIds || typeof r.createdAt !== 'number' || !Number.isFinite(r.createdAt)) return null
  const collectionId = typeof r.collectionId === 'string' ? r.collectionId : null
  return { nonce: r.nonce, ownerKey: r.ownerKey, binderIds, deckIds, collectionId, createdAt: r.createdAt }
}

/** Every stored launch, newest first; [] when the store is unreadable. */
export function readScanLaunches(): ScanLaunchRecord[] {
  let raw: string | null
  try {
    raw = window.localStorage.getItem(SCAN_LAUNCH_KEY)
  } catch {
    return []
  }
  if (!raw) return []
  try {
    const list: unknown = JSON.parse(raw)
    if (!Array.isArray(list)) return []
    return list.map(readRecord).filter((r): r is ScanLaunchRecord => r !== null)
  } catch {
    return []
  }
}

/**
 * Remember a launch (kept with the newest SCAN_LAUNCH_KEEP - 1 others, expired
 * ones dropped). Returns false when it could not be written: the scanner
 * still opens, and its batch will ask for a tap.
 */
export function recordScanLaunch(record: ScanLaunchRecord, now: number = Date.now()): boolean {
  const kept = readScanLaunches().filter(r => r.nonce !== record.nonce && live(r, now))
  const next = [record, ...kept].slice(0, SCAN_LAUNCH_KEEP)
  try {
    window.localStorage.setItem(SCAN_LAUNCH_KEY, JSON.stringify(next))
    return true
  } catch {
    return false
  }
}

function live(r: ScanLaunchRecord, now: number): boolean {
  // A record from the future (a clock moved back) is not trusted either.
  return r.createdAt <= now + 60_000 && now - r.createdAt < SCAN_LAUNCH_TTL_MS
}

/** The live launch with this nonce for this owner, or null. */
export function findScanLaunch(nonce: string, ownerKey: string, now: number = Date.now()): ScanLaunchRecord | null {
  if (!nonce) return null
  return readScanLaunches().find(r => r.nonce === nonce && r.ownerKey === ownerKey && live(r, now)) ?? null
}

/**
 * May a batch with this destination write without a tap? Only when its nonce
 * names a live launch of this profile for `ownerKey` and (a binder or a deck)
 * its id was one of that launch's targets. A NEW binder (`new: true`, no id)
 * has no target to match: the nonce and the owner are the whole check. A
 * launch made with no settled owner wrote no record, so its batch asks.
 */
export function isTrustedScanDestination(
  dest: { kind: string; id?: string | null; nonce: string; new?: boolean } | null | undefined,
  ownerKey: string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!dest || !ownerKey) return false
  const record = findScanLaunch(dest.nonce, ownerKey, now)
  if (!record) return false
  if (dest.kind === KIND_COLLECTION) return true
  if (dest.kind === KIND_BINDER && dest.new === true) return !dest.id
  if (!dest.id) return false
  if (dest.kind === KIND_BINDER) return record.binderIds.includes(dest.id)
  if (dest.kind === KIND_DECK) return record.deckIds.includes(dest.id)
  return false
}

/**
 * The launch owner key from collectionOwnerOf's answer: the user id, 'guest'
 * for a guest, null while auth is still loading (no record is written, and
 * nothing is trusted, until it has settled).
 */
export function scanOwnerKey(owner: string | null | undefined): string | null {
  if (owner === undefined) return null
  // eslint-disable-next-line i18next/no-literal-string -- an owner key code
  return owner ?? 'guest'
}
